import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import PricingTiersPicker from './PricingTiersPicker';
import { PLAN_ORDER, UPGRADE_PLAN_ORDER } from '../lib/plans';

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { initiatePayment, hasAccess, accessTier, hasUsedTrial, isLoading } = usePayment();

  const handleSelect = async (planId) => {
    if (!isAuthenticated) {
      navigate(`/auth?redirect=/pricing`);
      return;
    }
    await initiatePayment(planId, 'pricing-page');
  };

  // Hide trial option for users who've already used it.
  const plans = hasUsedTrial && accessTier !== 'trial' ? UPGRADE_PLAN_ORDER : PLAN_ORDER;

  return (
    <div className="min-h-screen bg-white">
      <header className="w-full px-6 py-6 fixed top-0 left-0 right-0 z-50 backdrop-blur-sm">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="bg-gray-100/80 backdrop-blur-sm rounded-full px-6 py-3 flex items-center space-x-4 shadow-sm border border-white/20">
            <h1 className="text-xl font-bold text-black">Valyfy</h1>
            <span className="text-black/40">|</span>
            <span className="text-sm text-black/60">Pricing</span>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="p-3 hover:bg-black/5 rounded-full transition-colors text-black/60"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="font-jersey text-4xl md:text-6xl text-black leading-tight mb-4">
            Pick a plan that fits
          </h1>
          <p className="text-black/60 text-lg max-w-2xl mx-auto">
            Start with £1 for your first month, go unlimited monthly, or own it for life.
          </p>
        </motion.div>

        {hasAccess && accessTier !== 'trial' && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-vibe-cyan/10 border border-vibe-cyan/30 rounded-xl text-center">
            <p className="text-sm text-black/70">
              You're already on the <strong>{accessTier === 'lifetime' ? 'Lifetime' : 'Pro'}</strong> plan. 🎉
            </p>
          </div>
        )}

        {!isLoading && (
          <PricingTiersPicker
            plans={plans}
            context="pricing-page"
            onSelect={handleSelect}
          />
        )}

        {hasUsedTrial && accessTier !== 'trial' && (
          <p className="text-center text-xs text-black/40 mt-4">
            You've already used your £1 intro — pick Pro or Lifetime to continue.
          </p>
        )}
      </main>
    </div>
  );
};

export default Pricing;
